import React, { useEffect } from "react";
import { Button, Modal, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Input from "components/Input";

import { useAppDispatch } from "store/hooks";

import { Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  handleAddCatalogBrandRequest,
  handleAddCatalogCategorieRequest,
} from "store/modules/Catalog/thunk.store";
import * as Styled from "./styles";

interface AddNewData {
  name: string;
  description: string;
}

interface AddNewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

const AddNewModal: React.FC<AddNewModalProps> = ({
  isOpen,
  title,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddNewData>();

  const dispatch = useAppDispatch();

  const isCategory = title === "Categoria";
  const isBrand = title === "Marca";

  const label = () => {
    if (isCategory) return t("category");
    if (isBrand) return t("brand");
    return "";
  };

  const handleSubmitForm = async (data: AddNewData) => {
    if (isCategory) {
      await dispatch(
        handleAddCatalogCategorieRequest({
          data: {
            name: data.name,
            description: data.description,
          },
        }),
      ).unwrap();
    }
    if (isBrand) {
      await dispatch(
        handleAddCatalogBrandRequest({
          data: {
            name: data.name,
            description: data.description,
          },
        }),
      ).unwrap();
    }

    onClose();
  };

  useEffect(() => {
    reset();
  }, [isOpen, reset]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Styled.Content>
        <Typography variant="h6" mb={2}>
          {`${t("add")} ${label()}`}
        </Typography>

        <Styled.TypeLinkContainer onSubmit={handleSubmit(handleSubmitForm)}>
          <Input
            name="name"
            error={errors.name}
            label={`${t("name")} ${t("of")} ${label()}`}
            register={register}
            fullWidth
            registerOptions={{
              required: t("requiredField"),
            }}
            sx={{ mb: 2 }}
          />

          <Input
            name="description"
            error={errors.description}
            label={`${t("title")} ${t("of")} ${label()}`}
            register={register}
            fullWidth
            registerOptions={{
              required: t("requiredField"),
            }}
            sx={{ mb: 2 }}
          />

          <Styled.ActionsContainer>
            <Button
              color="secondary"
              variant="text"
              onClick={onClose}
              endIcon={<CloseIcon />}
            >
              {t("close")}
            </Button>
            <Button variant="text" type="submit" endIcon={<SaveIcon />}>
              {t("save")}
            </Button>
          </Styled.ActionsContainer>
        </Styled.TypeLinkContainer>
      </Styled.Content>
    </Modal>
  );
};

export default AddNewModal;
